/**
 * DocsLibraryDrawer — the "Show the docs" panel (Lovable's "Show the code" equivalent).
 *
 * Slides in from the right and exposes the underlying business docs of a venture
 * (deck, personas, business models, network, investors, grants). The visible UI
 * preview is the brand-skinned cockpit; this drawer is the hidden Library.
 *
 * It reuses ALL existing renderers from MonetizeView (DataroomRender,
 * PersonasRender, …) so nothing is duplicated. Each module exposes a
 * Generate / Regenerate action so the user can build out the docs library
 * piece by piece directly from the drawer.
 */
import React, { useState } from 'react';
import {
  X, FileText, Users, Layers, Globe2, Briefcase, Award, Megaphone,
  Lock, Sparkles, RefreshCw, Loader2, CheckCircle2, Plug, ArrowRight,
} from 'lucide-react';

export default function DocsLibraryDrawer({
  venture,
  outputs,
  modules,
  palette,
  busyModule,
  onGenerate,
  onClose,
}) {
  const [openKey, setOpenKey] = useState(null);

  return (
    <div
      onClick={onClose}
      data-testid="docs-library-overlay"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(6px)', zIndex: 90,
        display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end',
        animation: 'fadeIn 0.18s ease-out',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        data-testid="docs-library-drawer"
        style={{
          width: '100%', maxWidth: 900, height: '100%',
          background: '#FFFFFF',
          borderLeft: `1px solid ${palette.border}`,
          boxShadow: '-24px 0 60px rgba(15,15,30,0.22)',
          display: 'flex', flexDirection: 'column',
          color: '#0F0F1A',
          animation: 'zoneSlideIn 0.35s cubic-bezier(0.16,1,0.3,1) both',
        }}
      >
        <div style={{
          padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 14,
          borderBottom: '1px solid rgba(15,15,30,0.06)',
        }}>
          <div style={{
            width: 36, height: 36,
            background: `${palette.accent}1F`, color: palette.accent,
            border: `1px solid ${palette.accent}55`,
            borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <FileText size={16} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase',
              color: 'rgba(15,15,30,0.5)',
            }}>
              Library · what's behind the preview
            </div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0F0F1A' }}>
              {venture?.name} · Docs
            </div>
          </div>
          <button onClick={onClose} data-testid="docs-library-close" style={{
            width: 30, height: 30,
            background: 'rgba(15,15,30,0.04)', border: '1px solid rgba(15,15,30,0.08)',
            color: 'rgba(15,15,30,0.6)', cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 6,
          }}>
            <X size={14} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
          <div style={{
            display: 'grid', gap: 10,
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          }}>
            {DOCS_MODULES.map(def => {
              const meta = modules?.[def.key] || { unlocked: false, status: 'locked', order: 99 };
              const cached = outputs?.[def.key];
              const isBusy = busyModule === def.key;
              return (
                <DocCard
                  key={def.key}
                  def={def}
                  meta={meta}
                  hasOutput={!!cached}
                  isBusy={isBusy}
                  palette={palette}
                  onGenerate={() => onGenerate?.(def.key, false)}
                  onRegenerate={() => onGenerate?.(def.key, true)}
                  onOpen={() => setOpenKey(def.key)}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* Inner viewer for a specific doc — uses existing renderers */}
      {openKey && (
        <DocViewer
          docKey={openKey}
          venture={venture}
          outputs={outputs}
          palette={palette}
          onClose={() => setOpenKey(null)}
          onRegenerate={() => onGenerate?.(openKey, true)}
          busy={busyModule === openKey}
        />
      )}
    </div>
  );
}

export const DOCS_MODULES = [
  { key: 'readme', label: 'README', headline: 'Build summary · ground truth',
    blurb: 'What you actually shipped — real name, features, pricing, design tone. Reads from the preview + repo. Every other doc is generated FROM this.',
    Icon: Sparkles, color: '#5EEAD4' },
  { key: 'dataroom', label: 'Deck', headline: 'Investor-grade narrative',
    blurb: 'Problem · Solution · Market · SWOT · Competitors · Forecast.',
    Icon: FileText, color: '#FFD700' },
  { key: 'personas', label: 'ICP', headline: 'Buyer surfaces',
    blurb: '4 sharp ICPs with first-feedback quotes & willingness-to-pay.',
    Icon: Users, color: '#06B6D4' },
  { key: 'business_models', label: 'Business models', headline: '5 models, ranked',
    blurb: 'Subscription · Lifetime · Usage · Sponsorship · Marketplace.',
    Icon: Layers, color: '#A855F7' },
  { key: 'network', label: 'Network', headline: 'Lead-gen playbook',
    blurb: 'LinkedIn · X · Instagram — warm leads & first DMs.',
    Icon: Globe2, color: '#22C55E', isNetwork: true },
  { key: 'investors', label: 'Investors', headline: 'Curated VC list',
    blurb: '8-12 funds & angels matched to stage, sector, geography.',
    Icon: Briefcase, color: '#3B82F6' },
  { key: 'grants', label: 'Grants', headline: 'Non-dilutive cash',
    blurb: 'Government grants & competitions you qualify for.',
    Icon: Award, color: '#FF6B6B' },
  { key: 'distribution', label: 'Distribution', headline: 'Outreach engine',
    blurb: 'Hermes + Naomi + Iris orchestrate cold DMs · threads · PH · SEO.',
    Icon: Megaphone, color: '#F97316', comingSoon: true },
];

function DocCard({ def, meta, hasOutput, isBusy, palette, onGenerate, onRegenerate, onOpen }) {
  const { Icon, color, label, headline, blurb, comingSoon, isNetwork } = def;
  const locked = !meta.unlocked;
  const complete = meta.status === 'complete' && hasOutput;
  // Aria is the sole generator now. The card never shows a manual "Generate"
  // CTA — only a status pill (Drafting / Ready / Soon) plus a View action
  // when the doc is complete. Network keeps its own "Connect & analyze" CTA
  // because it requires the founder's social tokens.
  const drafting = !complete && !comingSoon && !locked && isBusy;

  return (
    <div
      data-testid={`doc-card-${def.key}`}
      style={{
        padding: 16,
        background: complete ? `${color}08` : 'rgba(15,15,30,0.02)',
        border: complete ? `1px solid ${color}55` : '1px solid rgba(15,15,30,0.08)',
        borderRadius: 12,
        opacity: locked && !drafting ? 0.6 : 1,
        display: 'flex', flexDirection: 'column', gap: 9,
        position: 'relative', overflow: 'hidden',
      }}
    >
      {drafting && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 2,
          backgroundImage: `linear-gradient(90deg, transparent, ${color}, transparent)`,
          backgroundSize: '200% 100%',
          animation: 'tractionShimmer 1.6s linear infinite',
        }} />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 30, height: 30, flexShrink: 0,
          background: `${color}1F`, color,
          border: `1px solid ${color}40`,
          borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {locked && !drafting ? <Lock size={14} /> : <Icon size={14} />}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(15,15,30,0.48)' }}>
            Step {meta.order}
          </div>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0F0F1A' }}>{label}</div>
        </div>
        <StatusPill complete={complete} drafting={drafting} comingSoon={comingSoon} isNetwork={isNetwork} color={color} />
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#0F0F1A' }}>{headline}</div>
      <div style={{ fontSize: 11, color: 'rgba(15,15,30,0.6)', lineHeight: 1.55 }}>{blurb}</div>
      <div style={{ display: 'flex', gap: 6, marginTop: 'auto', paddingTop: 4 }}>
        {complete ? (
          <button
            onClick={onOpen}
            data-testid={`doc-open-${def.key}`}
            style={{
              flex: 1, padding: '7px 11px',
              background: `${color}18`, color, border: `1px solid ${color}55`,
              cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 700,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              letterSpacing: '0.05em', textTransform: 'uppercase',
              borderRadius: 6,
            }}
          >
            View <ArrowRight size={10} />
          </button>
        ) : comingSoon ? (
          <button disabled style={dimBtn}>Coming soon</button>
        ) : isNetwork ? (
          <button
            onClick={onOpen}
            data-testid={`doc-gen-${def.key}`}
            style={primaryBtn(color)}
          >
            <Plug size={11} /> Connect & analyze
          </button>
        ) : drafting ? (
          <button disabled style={{ ...dimBtn, color }}>
            <Loader2 size={11} style={{ animation: 'spin 0.7s linear infinite', marginRight: 5, verticalAlign: '-2px' }} />
            Aria is drafting
          </button>
        ) : locked ? (
          <button disabled style={dimBtn}>Aria queued</button>
        ) : (
          <button disabled style={dimBtn}>
            <Sparkles size={11} style={{ marginRight: 5, verticalAlign: '-2px' }} />
            Aria will draft this
          </button>
        )}
      </div>
    </div>
  );
}

function StatusPill({ complete, drafting, comingSoon, isNetwork, color }) {
  const cfg = comingSoon
    ? { label: 'Soon',     bg: 'rgba(15,15,30,0.06)',  fg: 'rgba(15,15,30,0.55)' }
    : complete
    ? { label: 'Ready',    bg: `${color}22`,           fg: color }
    : drafting
    ? { label: 'Drafting', bg: `${color}1A`,           fg: color }
    : isNetwork
    ? { label: 'Manual',   bg: 'rgba(34,197,94,0.12)', fg: '#22C55E' }
    : { label: 'Queued',   bg: 'rgba(15,15,30,0.06)',  fg: 'rgba(15,15,30,0.55)' };
  return (
    <span style={{
      fontSize: 8, fontWeight: 800, padding: '3px 7px',
      letterSpacing: '0.1em', textTransform: 'uppercase',
      background: cfg.bg, color: cfg.fg,
      border: `1px solid ${cfg.fg}33`,
      borderRadius: 4, whiteSpace: 'nowrap',
    }}>
      {complete && <CheckCircle2 size={9} style={{ verticalAlign: '-1px', marginRight: 3 }} />}
      {cfg.label}
    </span>
  );
}

const dimBtn = {
  flex: 1, padding: '7px 11px', cursor: 'not-allowed',
  background: 'rgba(15,15,30,0.035)', color: 'rgba(15,15,30,0.48)',
  border: '1px solid rgba(15,15,30,0.08)',
  fontFamily: 'inherit', fontSize: 11, fontWeight: 700,
  letterSpacing: '0.05em', textTransform: 'uppercase',
  borderRadius: 6,
};

const primaryBtn = (color) => ({
  flex: 1, padding: '7px 11px',
  background: color, color: '#FFFFFF', border: 'none',
  cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 700,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
  letterSpacing: '0.05em', textTransform: 'uppercase',
  borderRadius: 6,
});

/* DocViewer — renders a single doc using a renderer pluggable from MonetizeView.
   Implementation lives in MonetizeView so we avoid duplicating renderers here. */
function DocViewer({ docKey, venture, outputs, palette, onClose, onRegenerate, busy }) {
  const RendererRegistry = window.__monetizeRenderers || {};
  const Renderer = RendererRegistry[docKey];
  const cached = outputs?.[docKey];
  const def = DOCS_MODULES.find(d => d.key === docKey);
  const Icon = def?.Icon || FileText;
  const color = def?.color || palette.accent;

  return (
    <div
      onClick={onClose}
      data-testid="doc-viewer-overlay"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(8px)', zIndex: 110,
        display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 920, height: '100%',
          background: '#FFFFFF',
          borderLeft: `1px solid ${color}40`,
          boxShadow: '-24px 0 60px rgba(15,15,30,0.22)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          color: '#0F0F1A',
        }}
      >
        <div style={{
          padding: 18, borderBottom: '1px solid rgba(15,15,30,0.06)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 36, height: 36,
            background: `${color}1F`, color, border: `1px solid ${color}40`,
            borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon size={16} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: 'rgba(15,15,30,0.48)',
            }}>Aria output</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0F0F1A' }}>{def?.label}</div>
          </div>
          {cached && (
            <button
              onClick={onRegenerate} disabled={busy}
              data-testid="doc-viewer-regen"
              style={{
                padding: '7px 12px', fontSize: 10, fontWeight: 700,
                background: 'rgba(15,15,30,0.035)', color: 'rgba(15,15,30,0.72)',
                border: '1px solid rgba(15,15,30,0.1)', cursor: busy ? 'wait' : 'pointer',
                fontFamily: 'inherit', letterSpacing: '0.05em', textTransform: 'uppercase',
                display: 'inline-flex', alignItems: 'center', gap: 5,
                borderRadius: 6,
              }}
            >
              {busy ? <Loader2 size={11} style={{ animation: 'spin 0.7s linear infinite' }} /> : <RefreshCw size={11} />} Regenerate
            </button>
          )}
          <button onClick={onClose} data-testid="doc-viewer-close" style={{
            width: 30, height: 30,
            background: 'rgba(15,15,30,0.04)', border: '1px solid rgba(15,15,30,0.08)',
            color: 'rgba(15,15,30,0.6)', cursor: 'pointer', fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 6,
          }}>
            <X size={14} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 22 }}>
          {!cached ? (
            <div style={{ padding: 30, textAlign: 'center', color: 'rgba(15,15,30,0.5)', fontSize: 12 }}>
              No data yet. Generate this doc from the cards.
            </div>
          ) : Renderer ? (
            <Renderer payload={cached.data} cached={cached} color={color} venture={venture} />
          ) : (
            <pre style={{ fontSize: 11, color: 'rgba(15,15,30,0.65)', whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(cached.data, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
